import { useMemo, useState } from 'react'
import { Loader2, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AdminUsersFilters } from '@/components/AdminUsers/AdminUsersFilters'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { CreateAssignmentDialog } from '@/components/AdminReportPermissions/CreateAssignmentDialog'
import {
  useDeleteReportPermission,
  useReportAssignments,
  useUserAssignments,
} from '@/hooks/useReportPermissions'
import { POWERBI_REPORTS, WORKSPACE_LABELS } from '@/config/powerbiReports'
import type {
  AssignedReport,
  ReportAssignment,
  ReportPermissionUser,
  ReportType,
  UserAssignment,
} from '@/api/reportPermissions'
import type { UserRole } from '@/types/adminUser'

type ViewMode = 'by-report' | 'by-user'

interface PendingDeletion {
  permissionId: string
  userLabel: string
  reportLabel: string
}

const REPORT_LABEL_BY_ID: Record<string, string> = (() => {
  const map: Record<string, string> = {}
  for (const r of POWERBI_REPORTS.IFP) map[r.reportId] = r.label
  for (const r of POWERBI_REPORTS.CAT) map[r.reportId] = r.label
  return map
})()

function reportLabel(reportId: string): string {
  return REPORT_LABEL_BY_ID[reportId] ?? reportId
}

function reportTypeBadgeVariant(type: ReportType): 'info' | 'seasonal' {
  return type === 'IFP' ? 'info' : 'seasonal'
}

function userDisplayName(user: {
  display_name: string | null
  email: string
}): string {
  return user.display_name ?? user.email
}

export default function AdminReportPermissions() {
  const [viewMode, setViewMode] = useState<ViewMode>('by-report')
  const [searchInput, setSearchInput] = useState('')
  const [roleFilter, setRoleFilter] = useState<UserRole | 'all'>('all')
  const [createOpen, setCreateOpen] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<PendingDeletion | null>(
    null
  )

  const byReport = useReportAssignments()
  const byUser = useUserAssignments()
  const deleteMutation = useDeleteReportPermission()

  const isLoading = byReport.isLoading || byUser.isLoading

  const lowerSearch = searchInput.trim().toLowerCase()

  const userMatches = (user: {
    display_name: string | null
    email: string
    role: string
  }) => {
    if (lowerSearch) {
      const haystack = `${user.display_name ?? ''} ${user.email}`.toLowerCase()
      if (!haystack.includes(lowerSearch)) return false
    }
    if (roleFilter !== 'all' && user.role !== roleFilter) return false
    return true
  }

  const filteredReportAssignments = useMemo<ReportAssignment[]>(() => {
    return (byReport.data ?? []).map(r => ({
      ...r,
      users: r.users.filter(userMatches),
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byReport.data, lowerSearch, roleFilter])

  const filteredUserAssignments = useMemo<UserAssignment[]>(() => {
    return (byUser.data ?? []).filter(userMatches)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byUser.data, lowerSearch, roleFilter])

  const requestDelete = (
    permissionId: string,
    userLabel: string,
    reportLabelText: string
  ) => {
    setPendingDelete({
      permissionId,
      userLabel,
      reportLabel: reportLabelText,
    })
  }

  const confirmDelete = () => {
    if (!pendingDelete) return
    deleteMutation.mutate(pendingDelete.permissionId, {
      onSuccess: () => setPendingDelete(null),
    })
  }

  return (
    <>
      <ConfirmationDialog
        open={pendingDelete !== null}
        onOpenChange={open => {
          if (!open) setPendingDelete(null)
        }}
        title="Remove report access"
        description={
          pendingDelete
            ? `Remove access to "${pendingDelete.reportLabel}" for ${pendingDelete.userLabel}?`
            : ''
        }
        confirmText="Remove access"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />

      <CreateAssignmentDialog open={createOpen} onOpenChange={setCreateOpen} />

      <div className="flex flex-col gap-6 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Manage Report Permissions
            </h1>
            <p className="text-base text-muted-foreground mt-1">
              Grant or revoke per-report access for users on the Flow Tool and
              Category Assortment Tool dashboards.
            </p>
          </div>
          <Button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="shrink-0"
          >
            <Plus className="h-4 w-4" />
            Add assignment
          </Button>
        </div>

        <Tabs value={viewMode} onValueChange={v => setViewMode(v as ViewMode)}>
          <TabsList>
            <TabsTrigger value="by-report">By Report</TabsTrigger>
            <TabsTrigger value="by-user">By User</TabsTrigger>
          </TabsList>

          <AdminUsersFilters
            searchInput={searchInput}
            onSearchChange={setSearchInput}
            roleFilter={roleFilter}
            onRoleFilterChange={setRoleFilter}
          />

          {isLoading ? (
            <div className="flex min-h-60 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <TabsContent value="by-report" className="flex flex-col gap-4">
                <ReportsView
                  assignments={filteredReportAssignments}
                  onRemove={requestDelete}
                />
              </TabsContent>

              <TabsContent value="by-user" className="flex flex-col gap-4">
                <UsersView
                  assignments={filteredUserAssignments}
                  onRemove={requestDelete}
                />
              </TabsContent>
            </>
          )}
        </Tabs>
      </div>
    </>
  )
}

interface ReportsViewProps {
  assignments: ReportAssignment[]
  onRemove: (
    permissionId: string,
    userLabel: string,
    reportLabel: string
  ) => void
}

function ReportsView({ assignments, onRemove }: ReportsViewProps) {
  if (assignments.length === 0) {
    return <EmptyState message="No reports have been granted yet." />
  }

  return (
    <>
      {assignments.map(report => (
        <Card key={report.report_id}>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <span>{reportLabel(report.report_id)}</span>
                <Badge variant={reportTypeBadgeVariant(report.report_type)}>
                  {WORKSPACE_LABELS[report.report_type]}
                </Badge>
              </CardTitle>
              <span className="text-xs text-muted-foreground">
                {report.users.length}{' '}
                {report.users.length === 1 ? 'user' : 'users'}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {report.users.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No users match the current filters.
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-border">
                {report.users.map(user => (
                  <UserRow
                    key={user.permission_id}
                    user={user}
                    onRemove={() =>
                      onRemove(
                        user.permission_id,
                        userDisplayName(user),
                        reportLabel(report.report_id)
                      )
                    }
                  />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ))}
    </>
  )
}

interface UsersViewProps {
  assignments: UserAssignment[]
  onRemove: (
    permissionId: string,
    userLabel: string,
    reportLabel: string
  ) => void
}

function UsersView({ assignments, onRemove }: UsersViewProps) {
  if (assignments.length === 0) {
    return <EmptyState message="No users match the current filters." />
  }

  return (
    <>
      {assignments.map(user => (
        <Card key={user.user_id}>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <span>{userDisplayName(user)}</span>
                <Badge variant="neutral">{user.role}</Badge>
              </CardTitle>
              <span className="text-xs text-muted-foreground">
                {user.email} · {user.reports.length}{' '}
                {user.reports.length === 1 ? 'report' : 'reports'}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {user.reports.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No reports assigned.
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-border">
                {user.reports.map(r => (
                  <ReportRow
                    key={r.permission_id}
                    report={r}
                    onRemove={() =>
                      onRemove(
                        r.permission_id,
                        userDisplayName(user),
                        reportLabel(r.report_id)
                      )
                    }
                  />
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      ))}
    </>
  )
}

function UserRow({
  user,
  onRemove,
}: {
  user: ReportPermissionUser
  onRemove: () => void
}) {
  return (
    <li className="flex items-center justify-between gap-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <span className="truncate text-sm text-foreground">
          {userDisplayName(user)}
        </span>
        <Badge variant="neutral">{user.role}</Badge>
        <span className="truncate text-xs text-muted-foreground">
          {user.email}
        </span>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onRemove}
        aria-label="Remove access"
      >
        <X className="h-4 w-4" />
      </Button>
    </li>
  )
}

function ReportRow({
  report,
  onRemove,
}: {
  report: AssignedReport
  onRemove: () => void
}) {
  return (
    <li className="flex items-center justify-between gap-3 py-2">
      <div className="flex min-w-0 items-center gap-2">
        <span className="truncate text-sm text-foreground">
          {reportLabel(report.report_id)}
        </span>
        <Badge variant={reportTypeBadgeVariant(report.report_type)}>
          {WORKSPACE_LABELS[report.report_type]}
        </Badge>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={onRemove}
        aria-label="Remove access"
      >
        <X className="h-4 w-4" />
      </Button>
    </li>
  )
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-8 text-center">
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  )
}
